import React, { FC, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FileDto } from "@green0meter/api-client";

import { Box, Typography, TextField, Button } from "@components/base";
import { useSendChatMessage } from "@api/hooks/ai/SendChatMessage/useSendChatMessage";

interface AiChatProps {
  file: FileDto;
}

const AiChat: FC<AiChatProps> = ({ file }) => {
  const { t } = useTranslation();
  const [chatMessages, setChatMessages] = useState<
    { text: string; isUser: boolean }[]
  >([]);
  const [inputMessage, setInputMessage] = useState<string>("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const sendChatMessageMutation = useSendChatMessage();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const handleSendMessage = (): void => {
    if (inputMessage.trim()) {
      const userMessage = { text: inputMessage, isUser: true };
      const messageToSend = inputMessage;

      setChatMessages([...chatMessages, userMessage]);

      setInputMessage("");

      setChatMessages((prev) => [
        ...prev,
        {
          text: "...",
          isUser: false,
        },
      ]);

      sendChatMessageMutation.mutate(
        {
          prompt: messageToSend,
          fileId: file?.id ?? "",
          conversationHistory: [...chatMessages, userMessage],
        },
        {
          onSuccess: (response) => {
            setChatMessages((prev) => {
              const newMessages = [...prev];

              newMessages[newMessages.length - 1] = {
                text: response.response || "No response received",
                isUser: false,
              };

              return newMessages;
            });
          },
          onError: (error) => {
            setChatMessages((prev) => {
              const newMessages = [...prev];

              console.error(error);
              newMessages[newMessages.length - 1] = {
                text: "Error occurred while processing your request. Please try again.",
                isUser: false,
              };

              return newMessages;
            });
          },
        },
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Box sx={{ mt: 3, border: "1px solid #e0e0e0", borderRadius: 1, p: 2 }}>
      <Typography variant="h6" mb={2}>
        {t("ai.dockyName")}
      </Typography>

      <Box
        sx={{
          height: "130px",
          overflowY: "scroll",
          mb: 2,
          p: 2,
          bgcolor: "#f5f5f5",
          borderRadius: 1,
        }}
      >
        {chatMessages.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", mt: 10 }}
          >
            {t("ai.noMessages")}
          </Typography>
        ) : (
          <>
            {chatMessages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  mb: 1,
                  display: "flex",
                  justifyContent: message.isUser ? "flex-end" : "flex-start",
                }}
              >
                <Box
                  sx={{
                    maxWidth: "80%",
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: message.isUser ? "#fff" : "#e0e0e0",
                    color: message.isUser ? "white" : "black",
                  }}
                >
                  <Typography variant="body1">{message.text}</Typography>
                </Box>
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          fullWidth
          placeholder={t("ai.textPlaceholder")}
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          multiline
          variant="outlined"
        />
        <Button
          variant="contained"
          onClick={handleSendMessage}
          sx={{ alignSelf: "flex-end" }}
        >
          {t("common.send")}
        </Button>
      </Box>
    </Box>
  );
};

export default AiChat;
